import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Temporal } from 'temporal-polyfill';
import { buildIcs } from '../domain/ics';
import { formatSeriesSummary } from '../domain/format';
import { errorFor, isValidIsoDate, validateLocation } from '../domain/validation';
import type { LocationDraft, ValidationResult } from '../domain/validation';
import type { ClassSeries, Location } from '../domain/types';
import { useAsync, useRepository } from './repositoryContext';
import { EmptyState, ErrorBanner, Field } from './components';
import { ChevronRight } from './icons';
import { buildInfo } from './buildInfo';
import { persistenceState, storageUsage } from './storage';
import { browserTimeZone, friendlyZone } from './LocationField';
import {
  DEFAULT_REMINDER_MINUTES,
  REMINDER_CHOICES,
  REMINDER_ENABLED_KEY,
  REMINDER_MINUTES_KEY,
  usePreference,
  useNumberPreference,
} from './usePreference';

export interface ExportPayload {
  readonly fileName: string;
  readonly content: string;
}

function downloadInBrowser(payload: ExportPayload): void {
  const blob = new Blob([payload.content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = payload.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Hands the calendar file to the OS share sheet where supported (iOS and
 * Android: straight into the calendar app, AirDrop, mail…), falling back to
 * a plain download. Returns false when the user cancelled the share sheet.
 */
async function shareOrDownload(payload: ExportPayload): Promise<boolean> {
  const file = new File([payload.content], payload.fileName, {
    type: 'text/calendar',
  });
  const shareData: ShareData = { files: [file], title: 'Yoga Schedule' };
  if (
    typeof navigator.canShare === 'function' &&
    typeof navigator.share === 'function' &&
    navigator.canShare(shareData)
  ) {
    try {
      await navigator.share(shareData);
      return true;
    } catch (cause: unknown) {
      // Cancelling the share sheet is a decision, not a failure.
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        return false;
      }
      // Anything else (permission, platform quirk): the download still works.
    }
  }
  downloadInBrowser(payload);
  return true;
}

/**
 * Clamps a series to a date window.
 *
 * Export is series-based (one VEVENT with an RRULE), so a "date range" scope
 * narrows each series' own start and end rather than listing occurrences.
 */
export function clampSeries(series: ClassSeries, from: string, to: string): ClassSeries | null {
  const startDate = series.startDate > from ? series.startDate : from;
  const endDate = series.endDate === null || series.endDate > to ? to : series.endDate;
  return startDate > endDate ? null : { ...series, startDate, endDate };
}

export function MoreView(props: { download?: (payload: ExportPayload) => void }): JSX.Element {
  return (
    <section>
      <header class="screen-head">
        <h2>More</h2>
      </header>
      <PlacesSection />
      <CalendarSection download={props.download} />
      <AboutSection />
    </section>
  );
}

// -------------------------------------------------------------------- about

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function AboutSection(): JSX.Element {
  const storage = useAsync(
    async () => ({
      persistence: await persistenceState(),
      usage: await storageUsage(),
    }),
    [],
  );

  const built =
    buildInfo.builtAt === ''
      ? null
      : new Date(buildInfo.builtAt).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        });

  const persistence = storage.data?.persistence;
  const usage = storage.data?.usage ?? null;
  return (
    <div class="group">
      <h3 class="group-heading">About</h3>
      <p class="field-hint">
        Version {buildInfo.commit}
        {built === null ? '' : ` · built ${built}`}
      </p>
      {persistence !== undefined && persistence !== 'unsupported' ? (
        <p class="field-hint">
          Data stays on this device
          {usage === null ? '' : ` (${formatBytes(usage.usageBytes)} used)`}.{' '}
          {persistence === 'protected'
            ? 'The browser has agreed not to evict it.'
            : 'The browser may reclaim it if space runs low — installing the app protects it.'}
        </p>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------- places

function PlacesSection(): JSX.Element {
  const repo = useRepository();
  const locations = useAsync(() => repo.listLocations(), []);

  const [editing, setEditing] = useState<Location | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const all = locations.data ?? [];

  async function remove(location: Location): Promise<void> {
    // Places are referenced by classes, so removal is a tombstone rather than a
    // hard delete; the schedule then reports those classes as hidden.
    if (!confirm(`Remove "${location.name}"? Classes there will need a new place.`)) {
      return;
    }
    try {
      await repo.deleteLocation(location.id);
      locations.reload();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  if (adding || editing !== null) {
    return (
      <PlaceForm
        place={editing}
        onDone={() => {
          setAdding(false);
          setEditing(null);
          locations.reload();
        }}
        onCancel={() => {
          setAdding(false);
          setEditing(null);
        }}
        onDelete={
          editing === null
            ? undefined
            : async () => {
                await remove(editing);
                setEditing(null);
              }
        }
      />
    );
  }

  return (
    <div class="group">
      <h3 class="group-heading">Places</h3>
      <ErrorBanner error={locations.error ?? error} />

      {!locations.loading && all.length === 0 ? (
        <EmptyState title="No places yet.">
          <p>A place is added automatically when you name one on a class.</p>
        </EmptyState>
      ) : null}

      <ul class="rows">
        {all.map((location) => (
          <li key={location.id}>
            <button type="button" class="row" onClick={() => setEditing(location)}>
              <span class="row-what">
                <span class="row-title">{location.name}</span>
                <span class="row-place">{friendlyZone(location.timeZone)}</span>
              </span>
              <ChevronRight />
            </button>
          </li>
        ))}
      </ul>

      <button type="button" class="block" onClick={() => setAdding(true)}>
        + Add a place
      </button>
    </div>
  );
}

function PlaceForm(props: {
  place: Location | null;
  onDone: () => void;
  onCancel: () => void;
  onDelete?: (() => Promise<void>) | undefined;
}): JSX.Element {
  const repo = useRepository();
  const [draft, setDraft] = useState<LocationDraft>(() =>
    props.place === null
      ? { name: '', address: '', timeZone: browserTimeZone() }
      : {
          name: props.place.name,
          address: props.place.address,
          timeZone: props.place.timeZone,
        },
  );
  const [validation, setValidation] = useState<ValidationResult>({ ok: true });
  const [error, setError] = useState<Error | undefined>(undefined);

  function patch(changes: Partial<LocationDraft>): void {
    setDraft((current) => ({ ...current, ...changes }));
  }

  async function save(event: Event): Promise<void> {
    event.preventDefault();
    const trimmed: LocationDraft = {
      ...draft,
      name: draft.name.trim(),
      address: draft.address.trim(),
    };
    const result = validateLocation(trimmed);
    setValidation(result);
    if (!result.ok) {
      return;
    }
    try {
      if (props.place === null) {
        await repo.createLocation(trimmed);
      } else {
        await repo.updateLocation(props.place.id, trimmed);
      }
      props.onDone();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  return (
    <form class="screen" onSubmit={(event) => void save(event)}>
      <header class="screen-bar">
        <button type="button" class="bar-action" onClick={props.onCancel}>
          Cancel
        </button>
        <h2>{props.place === null ? 'New place' : 'Edit place'}</h2>
        <button type="submit" class="bar-action strong">
          Save
        </button>
      </header>

      <div class="screen-body">
        <ErrorBanner error={error} />

        <Field label="Name" error={errorFor(validation, 'name')}>
          <input
            type="text"
            placeholder="Studio A"
            value={draft.name}
            onInput={(event) => patch({ name: (event.target as HTMLInputElement).value })}
          />
        </Field>

        <Field label="Address" hint="Optional. Appears in the calendar export.">
          <input
            type="text"
            value={draft.address}
            onInput={(event) => patch({ address: (event.target as HTMLInputElement).value })}
          />
        </Field>

        <Field
          label="Timezone"
          error={errorFor(validation, 'timeZone')}
          hint="Class times are wall-clock times here. Full IANA name, e.g. America/Los_Angeles."
        >
          <input
            type="text"
            value={draft.timeZone}
            onInput={(event) => patch({ timeZone: (event.target as HTMLInputElement).value })}
          />
        </Field>

        {props.onDelete !== undefined ? (
          <div class="danger-zone">
            <button type="button" class="block danger" onClick={() => void props.onDelete?.()}>
              Remove this place
            </button>
          </div>
        ) : null}
      </div>
    </form>
  );
}

// ----------------------------------------------------------------- calendar

const SCOPES = [
  { id: 'all', label: 'Everything' },
  { id: 'one', label: 'One class' },
  { id: 'range', label: 'Date range' },
] as const;

type ScopeId = (typeof SCOPES)[number]['id'];

function CalendarSection(props: { download?: (payload: ExportPayload) => void }): JSX.Element {
  const repo = useRepository();
  const data = useAsync(
    async () => ({
      locations: await repo.listLocations(),
      series: await repo.listSeries(),
      overrides: await repo.listOverrides(),
      claims: await repo.listClaims(),
    }),
    [],
  );

  const today = useMemo(() => Temporal.Now.plainDateISO(), []);
  const [scope, setScope] = useState<ScopeId>('all');
  const [seriesId, setSeriesId] = useState('');
  const [from, setFrom] = useState(today.toString());
  const [to, setTo] = useState(today.add({ months: 3 }).toString());
  const [error, setError] = useState<Error | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [reminderEnabled, setReminderEnabled] = usePreference(REMINDER_ENABLED_KEY, true);
  const [reminderMinutes, setReminderMinutes] = useNumberPreference(
    REMINDER_MINUTES_KEY,
    DEFAULT_REMINDER_MINUTES,
  );

  const allSeries = data.data?.series ?? [];
  const activeSeries = allSeries.filter((item) => item.active);

  function selectedSeries(): ClassSeries[] {
    if (scope === 'one') {
      const chosen = allSeries.find((item) => item.id === seriesId) ?? activeSeries[0];
      return chosen === undefined ? [] : [chosen];
    }
    if (scope === 'range') {
      return activeSeries
        .map((item) => clampSeries(item, from, to))
        .filter((item): item is ClassSeries => item !== null);
    }
    return activeSeries;
  }

  async function exportNow(): Promise<void> {
    if (scope === 'range' && (!isValidIsoDate(from) || !isValidIsoDate(to) || from > to)) {
      setError(new Error('Pick a valid date range.'));
      return;
    }
    try {
      const series = selectedSeries();
      if (series.length === 0) {
        setError(new Error('Nothing to send for that selection.'));
        return;
      }
      const content = buildIcs({
        series,
        locations: data.data?.locations ?? [],
        overrides: data.data?.overrides ?? [],
        claims: data.data?.claims ?? [],
        now: Temporal.Now.instant(),
        calendarName: 'Yoga Schedule',
        reminderMinutesBefore: reminderEnabled ? reminderMinutes : null,
      });
      const payload: ExportPayload = {
        fileName: `yoga-schedule-${today.toString()}.ics`,
        content,
      };
      let delivered: boolean;
      if (props.download !== undefined) {
        props.download(payload);
        delivered = true;
      } else {
        delivered = await shareOrDownload(payload);
      }
      setError(undefined);
      if (delivered) {
        setStatus(`Sent ${series.length} ${series.length === 1 ? 'class' : 'classes'}.`);
      }
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  return (
    <div class="group">
      <h3 class="group-heading">Calendar</h3>
      <ErrorBanner error={data.error ?? error} />

      {data.loading && data.data === undefined ? <p class="muted">Loading…</p> : null}

      {data.data !== undefined && activeSeries.length === 0 ? (
        <EmptyState title="Nothing to send yet.">
          <p>Add a class first.</p>
        </EmptyState>
      ) : null}

      {/* Rendering the button before data arrives would let a fast tap export nothing. */}
      {data.data !== undefined && activeSeries.length > 0 ? (
        <div class="stack">
          <div class="segmented" role="group" aria-label="What to send">
            {SCOPES.map((option) => (
              <button
                key={option.id}
                type="button"
                class={`segment${scope === option.id ? ' selected' : ''}`}
                aria-pressed={scope === option.id}
                onClick={() => setScope(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {scope === 'one' ? (
            <Field label="Class">
              <select
                value={seriesId}
                onChange={(event) => setSeriesId((event.target as HTMLSelectElement).value)}
              >
                {activeSeries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title} —{' '}
                    {formatSeriesSummary({
                      recurrence: item.recurrence,
                      startTime: item.startTime,
                    })}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          {scope === 'range' ? (
            <div class="inline-row">
              <Field label="From">
                <input
                  type="date"
                  value={from}
                  onInput={(event) => setFrom((event.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="To">
                <input
                  type="date"
                  value={to}
                  onInput={(event) => setTo((event.target as HTMLInputElement).value)}
                />
              </Field>
            </div>
          ) : null}

          <Field label="Reminder">
            <label class="check-row">
              <input
                type="checkbox"
                checked={reminderEnabled}
                onChange={(event) => setReminderEnabled((event.target as HTMLInputElement).checked)}
              />
              <span>Remind me before each class</span>
            </label>
          </Field>

          {reminderEnabled ? (
            <Field label="How early">
              <select
                value={String(reminderMinutes)}
                onChange={(event) =>
                  setReminderMinutes(Number((event.target as HTMLSelectElement).value))
                }
              >
                {REMINDER_CHOICES.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {minutes < 60 ? `${minutes} minutes before` : '1 hour before'}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          <button type="button" class="block primary" onClick={() => void exportNow()}>
            Send to my calendar
          </button>

          {status !== undefined ? (
            <p class="summary" role="status">
              {status}
            </p>
          ) : null}

          <p class="field-hint">
            {reminderEnabled
              ? `Each class carries a reminder ${
                  reminderMinutes < 60 ? `${reminderMinutes} minutes` : '1 hour'
                } before it starts, shown by your calendar app. `
              : 'Reminders are off. '}
            This sends a file — if you change a class later, your calendar won't update on its own;
            send it again.
          </p>
        </div>
      ) : null}
    </div>
  );
}
